import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CaretRight, ListChecks, Warning } from '@phosphor-icons/react';
import {
  Box,
  Flex,
  Text,
  TagBadge,
  UnderlineTab,
  UnderlineTabs,
  type TagColor,
} from '@chatsdet/ui';
import type { ExploratoryBrowserSession, ReviewFinding, SpecReviewReportJson, SpecReviewSpecItem } from '../types';
import { ReviewFindingsList } from './ReviewFindingsList';

// ── Badge helpers ────────────────────────────────────────────────────────────
// Pass/fail-shaped verdicts use the repo's blue/orange convention (never
// green/red); partial/conditional verdicts sit at yellow in between.

const TESTABLE_META: Record<SpecReviewSpecItem['testable'], { label: string; color: TagColor }> = {
  yes: { label: 'Testable', color: 'blue' },
  partial: { label: 'Partial', color: 'yellow' },
  no: { label: 'Not testable', color: 'orange' },
};

export const CONVERTIBLE_META: Record<
  SpecReviewReportJson['flowReadiness']['convertible'],
  { label: string; color: TagColor }
> = {
  yes: { label: 'Convertible', color: 'blue' },
  with_changes: { label: 'Convertible with changes', color: 'yellow' },
  no: { label: 'Not convertible', color: 'orange' },
};

/** Finding-group taxonomy — mirrors SPEC_DIMENSIONS in apps/api/src/adhoc-test/flavors/spec.ts. */
const SPEC_FINDING_DIMENSIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'untestable', label: 'Untestable as written' },
  { key: 'assumption_failed', label: 'Assumption failed' },
  { key: 'data_dependency', label: 'Data dependency' },
  { key: 'idempotency_risk', label: 'Idempotency risk' },
  { key: 'environment_gap', label: 'Environment gap' },
];

const specDimensionLabel = (key: string) =>
  SPEC_FINDING_DIMENSIONS.find((d) => d.key === key)?.label;

const SPEC_DIMENSION_ORDER = SPEC_FINDING_DIMENSIONS.map((d) => d.key);

// ── Small disclosures ────────────────────────────────────────────────────────
// Minimal expand/collapse matching the trigger/chevron/AnimatePresence idiom
// already used by ReviewFindingsList's FindingRow — collapsed by default so
// the panel stays scannable.

function BlockersDisclosure({ blockers }: { blockers: string[] }) {
  const [expanded, setExpanded] = React.useState(false);
  if (blockers.length === 0) return null;

  return (
    <Box>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
      >
        <CaretRight
          size={11}
          weight="bold"
          className={`shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
        />
        <Text size="xs" className="text-muted-foreground">
          {blockers.length} blocker{blockers.length === 1 ? '' : 's'}
        </Text>
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <ul className="mt-2 ml-4 list-disc space-y-1">
              {blockers.map((blocker, i) => (
                <li key={i}>
                  <Text size="xs" className="text-muted-foreground">
                    {blocker}
                  </Text>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}

function SpecItemRow({ item }: { item: SpecReviewSpecItem }) {
  const [expanded, setExpanded] = React.useState(false);
  const meta = TESTABLE_META[item.testable];
  const hasDetail = Boolean(item.reason || item.evidence);

  return (
    <Box className="border-t border-border/40 pt-2 first:border-t-0 first:pt-0">
      <button
        type="button"
        onClick={() => hasDetail && setExpanded((v) => !v)}
        className="flex w-full items-start gap-2 text-left"
        disabled={!hasDetail}
      >
        <TagBadge name={meta.label} color={meta.color} hideDot />
        <Text size="sm" className="min-w-0 flex-1 leading-snug">
          {item.statement}
        </Text>
        {hasDetail && (
          <CaretRight
            size={13}
            weight="bold"
            className={`mt-1 shrink-0 text-muted-foreground transition-transform duration-200 ${
              expanded ? 'rotate-90' : ''
            }`}
          />
        )}
      </button>
      <AnimatePresence initial={false}>
        {expanded && hasDetail && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <Box className="pt-1 pl-1">
              {item.reason && (
                <Text size="xs" className="text-muted-foreground">
                  {item.reason}
                </Text>
              )}
              {item.evidence && (
                <Text size="xs" className="mt-0.5 text-muted-foreground/80 italic">
                  {item.evidence}
                </Text>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}

type SpecReviewSidebarTab = 'items' | 'findings';

// ── Panel ────────────────────────────────────────────────────────────────────

/**
 * Spec-review sidebar — the most important-at-a-glance info: a compact
 * always-visible overview (summary, counts, flow readiness, blockers-behind-
 * a-disclosure) followed by underline tabs for the per-item testability
 * breakdown and the shared findings list. The cleaned spec and research
 * ledger (assumptions/idempotency/data dependencies) now live in the main
 * view's tabs, where they get full-width reading room — see
 * `SpecReviewMainSections` and `SpecReviewMainTabs`.
 */
export function SpecReviewPanel({
  report,
  findings,
  runId,
  sessions,
  onSelectSession,
}: {
  report: SpecReviewReportJson | null;
  findings: ReviewFinding[];
  runId: string;
  sessions: ExploratoryBrowserSession[];
  onSelectSession: (id: string) => void;
}) {
  const [activeTab, setActiveTab] = React.useState<SpecReviewSidebarTab>('items');

  if (!report) {
    return (
      <Box className="flex h-full flex-col overflow-y-auto">
        <Box className="p-4">
          <Text size="sm" className="text-muted-foreground">
            No report yet — the run may still be in progress or did not complete.
          </Text>
        </Box>
        <ReviewFindingsList
          findings={findings}
          runId={runId}
          sessions={sessions}
          onSelectSession={onSelectSession}
          dimensionLabel={specDimensionLabel}
          dimensionOrder={SPEC_DIMENSION_ORDER}
        />
      </Box>
    );
  }

  const convertible = CONVERTIBLE_META[report.flowReadiness.convertible];

  return (
    <Box className="flex h-full flex-col overflow-hidden">
      {/* Compact overview header: summary + counts + flow readiness + blockers */}
      <Box className="border-b border-border p-3">
        <Text size="xs" className="uppercase tracking-wide text-muted-foreground">
          Spec review
        </Text>
        {report.summary && (
          <Text size="sm" className="mt-1 leading-snug text-foreground">
            {report.summary}
          </Text>
        )}

        <Flex align="center" gap="xs" className="mt-2 flex-wrap">
          <TagBadge name={`Testable ${report.counts.testable}`} color="blue" hideDot />
          <TagBadge name={`Partial ${report.counts.partial}`} color="yellow" hideDot />
          <TagBadge name={`Not testable ${report.counts.notTestable}`} color="orange" hideDot />
        </Flex>

        <Flex align="center" gap="xs" className="mt-2 flex-wrap">
          <Text size="xs" weight="medium" className="text-muted-foreground">
            Flow readiness:
          </Text>
          <TagBadge name={convertible.label} color={convertible.color} hideDot />
          <BlockersDisclosure blockers={report.flowReadiness.blockers} />
        </Flex>
      </Box>

      <UnderlineTabs
        selectedId={activeTab}
        onSelectionChange={(id) => setActiveTab(id as SpecReviewSidebarTab)}
        className="pt-3"
      >
        <UnderlineTab id="items" className="flex-1 py-3">
          <Flex align="center" gap="xs" justify="center">
            <ListChecks size={16} weight="bold" />
            Items · {report.specItems.length}
          </Flex>
        </UnderlineTab>
        <UnderlineTab id="findings" className="flex-1 py-3">
          <Flex align="center" gap="xs" justify="center">
            <Warning size={16} weight="bold" />
            Findings{findings.length > 0 ? ` · ${findings.length}` : ''}
          </Flex>
        </UnderlineTab>
      </UnderlineTabs>

      <Box className="flex-1 overflow-y-auto">
        {activeTab === 'items' && (
          <Flex direction="column" gap="sm" className="p-3">
            {report.specItems.map((item, i) => (
              <SpecItemRow key={i} item={item} />
            ))}
          </Flex>
        )}
        {activeTab === 'findings' && (
          <ReviewFindingsList
            findings={findings}
            runId={runId}
            sessions={sessions}
            onSelectSession={onSelectSession}
            dimensionLabel={specDimensionLabel}
            dimensionOrder={SPEC_DIMENSION_ORDER}
          />
        )}
      </Box>
    </Box>
  );
}
